import { useEffect, useState } from "react"
import { Layer, Source } from "react-map-gl/mapbox"
import centerOfMass from "@turf/center-of-mass"
import type { Feature, FeatureCollection } from "geojson"
import type { Expression } from "mapbox-gl"

const COLOR_PALETTES: Record<string, string[]> = {
  A: ["#FFFF00"],
  N: ["#00FF00"],
  U: ["#60A5FA", "#3B82F6", "#2563EB", "#1D4ED8", "#1E40AF", "#1E3A8A"],
  AUc: ["#DC2626", "#B91C1C", "#991B1B", "#7F1D1D", "#450A0A"],
  AUs: ["#FB923C", "#F97316", "#EA580C", "#C2410C", "#9A3412", "#7C2D12"],
}

const DEFAULT_FILL_COLOR = "#94A3B8"

interface CommunePluLayerProps {
  pluTypeZones: string[]
  pluLibelles?: string[]
  opacity?: number
  pluData?: Feature[]
}

export const CommunePluLayer = ({ pluTypeZones, pluLibelles, opacity, pluData }: CommunePluLayerProps) => {
  const [pluLayer, setPluLayer] = useState<FeatureCollection>()
  const [symbolLayer, setSymbolLayer] = useState<FeatureCollection>()

  const getFilteredPluData = async () => {
    const data: FeatureCollection = {
      type: "FeatureCollection",
      features: [],
    }

    const colorAssignments: Record<string, Record<string, string>> = {}
    const colorIndexes: Record<string, number> = {}

    const getNextColor = (typeZone: string, key: string) => {
      const palette = COLOR_PALETTES[typeZone]
      if (!palette || palette.length === 0) {
        return DEFAULT_FILL_COLOR
      }

      if (!colorAssignments[typeZone]) {
        colorAssignments[typeZone] = {}
        colorIndexes[typeZone] = 0
      }

      if (!colorAssignments[typeZone][key]) {
        const index = colorIndexes[typeZone] ?? 0
        colorAssignments[typeZone][key] = palette[index % palette.length]
        colorIndexes[typeZone] = (index + 1) % palette.length
      }

      return colorAssignments[typeZone][key]
    }

    data.features = (pluData ?? []).map((feature: Feature) => {
      const typeZone: string | undefined = feature?.properties?.typezone
      const libelle: string = feature?.properties?.libelle ?? ""
      const libelleKey = libelle || typeZone || "UNKNOWN"

      const isSelected =
        !!typeZone &&
        pluTypeZones.includes(typeZone) &&
        (!pluLibelles ||
          pluLibelles.length === 0 ||
          pluLibelles.some((pluLibelle: string) => libelle.startsWith(pluLibelle)))

      const fillColor = typeZone ? getNextColor(typeZone, libelleKey) : DEFAULT_FILL_COLOR

      return {
        ...feature,
        properties: {
          ...feature.properties,
          selected: isSelected,
          fillColor,
        },
      }
    })

    const symbols = (data?.features ?? [])
      .map((feature: Feature) => {
        try {
          const center = centerOfMass(feature)
          return {
            type: "Feature" as const,
            geometry: center.geometry,
            properties: {
              description: feature.properties?.libelle,
              selected: feature.properties?.selected,
              fillColor: feature.properties?.fillColor,
            },
          }
        } catch (error) {
          console.error("Failed to compute center for PLU feature", error)
          return null
        }
      })
      .filter(Boolean) as Feature[]

    setPluLayer(data)
    setSymbolLayer({
      type: "FeatureCollection",
      features: symbols,
    })
  }

  useEffect(() => {
    getFilteredPluData()
  }, [pluTypeZones, pluLibelles, pluData])

  const pluPaint: Record<string, Expression> = {
    "fill-color": ["coalesce", ["get", "fillColor"], DEFAULT_FILL_COLOR],
    "fill-opacity": ["case", ["==", ["get", "selected"], true], opacity ?? 0.5, 0],
  }

  return (
    <>
      <Source id="commune-plu" type="geojson" data={pluLayer}>
        <Layer id="commune-plu" type="fill" paint={pluPaint} />
        <Layer
          id="border"
          type="line"
          paint={{
            "line-color": [
              "case",
              ["==", ["get", "isFavourite"], true],
              "#FC814F",
              ["coalesce", ["get", "fillColor"], "#46A097"],
            ],
            "line-width": 2,
            "line-opacity": ["case", ["==", ["get", "selected"], true], opacity ?? 0.8, 0],
          }}
        />
      </Source>
      <Source id="commune-plu-symbol" type="geojson" data={symbolLayer}>
        <Layer
          minzoom={13}
          id="commune-plu-symbol"
          type="symbol"
          layout={{
            "text-field": ["get", "description"],
            "text-size": 18,
            "text-anchor": "center",
            "text-allow-overlap": true,
          }}
          paint={{
            "text-color": "#000",
            "text-halo-color": "#fff",
            "text-halo-width": 1,
            "text-opacity": ["case", ["==", ["get", "selected"], true], opacity === 0 ? 0 : 1, 0],
          }}
        />
      </Source>
    </>
  )
}
